import { z } from "zod";

import { CrmDealCreationResultSchema } from "./crmDealCreation";

export type { CrmDealCreationResult } from "./crmDealCreation";

// Entity types for field mappings
export enum HubspotEntityType {
	CONTACT = "CONTACT",
	COMPANY = "COMPANY",
	DEAL = "DEAL",
	LEAD = "LEAD",
}

// HubSpot field from API
export const HubspotFieldSchema = z.object({
	name: z.string(),
	label: z.string(),
	type: z.string(),
	fieldType: z.string().optional(),
	groupName: z.string().optional(),
	isCustom: z.boolean(),
	isRequired: z.boolean().optional(),
	isReadOnly: z.boolean().optional(),
	isCalculated: z.boolean().optional(),
	isMulti: z.boolean().optional(),
	/**
	 * HubSpot accepts this property only on the initial create (record-source details);
	 * updates are rejected, so the API strips it from PATCH payloads.
	 */
	isCreateOnly: z.boolean().optional(),
	options: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
});

export type HubspotField = z.infer<typeof HubspotFieldSchema>;

// Source field from our system
export const SourceFieldSchema = z.object({
	name: z.string(),
	label: z.string(),
	type: z.string(),
});

export type SourceField = z.infer<typeof SourceFieldSchema>;

// Source type for field mappings (FIELD = record path, CONSTANT = literal string, AI = AI-enriched)
export enum FieldMappingSourceType {
	FIELD = "FIELD",
	CONSTANT = "CONSTANT",
	AI = "AI",
}

export const FieldMappingAiConfigSchema = z.object({
	prompt: z.string().min(1),
	fallbackValue: z.string().optional(),
	syncReasoningTargetField: z.string().optional(),
	syncSourceTargetField: z.string().optional(),
});

export type FieldMappingAiConfig = z.infer<typeof FieldMappingAiConfigSchema>;

export const AiTestRequestSchema = z.object({
	prospectId: z.number(),
	entityType: z.string(),
	hubspotField: z.string().optional(),
	zohoField: z.string().optional(),
	pipedriveField: z.string().optional(),
	msdynamicsField: z.string().optional(),
	salesforceField: z.string().optional(),
	prompt: z.string(),
	allowedValues: z.array(z.string()).optional(),
	fallbackValue: z.string().optional(),
});

export type AiTestRequest = z.infer<typeof AiTestRequestSchema>;

export const AiTestResponseSchema = z.object({
	value: z.string(),
	reasoning: z.string().optional(),
	sources: z.array(z.string()).default([]),
	fallbackUsed: z.boolean().optional(),
	durationMs: z.number().optional(),
});

export type AiTestResponse = z.infer<typeof AiTestResponseSchema>;

// Field mapping
export const FieldMappingSchema = z.object({
	id: z.number(),
	sourceField: z.string(),
	sourceType: z.nativeEnum(FieldMappingSourceType),
	hubspotField: z.string(),
	entityType: z.nativeEnum(HubspotEntityType),
	isActive: z.boolean(),
	aiConfig: FieldMappingAiConfigSchema.optional(),
	createdAt: z.string().optional(),
	updatedAt: z.string().optional(),
});

export type FieldMapping = z.infer<typeof FieldMappingSchema>;

// Create field mapping DTO
export const CreateFieldMappingSchema = z.object({
	sourceField: z.string().min(1),
	sourceType: z.nativeEnum(FieldMappingSourceType).optional(),
	hubspotField: z.string().min(1),
	entityType: z.nativeEnum(HubspotEntityType),
	isActive: z.boolean().optional(),
	aiConfig: FieldMappingAiConfigSchema.optional(),
});

export type CreateFieldMapping = z.infer<typeof CreateFieldMappingSchema>;

// Update field mapping DTO
export const UpdateFieldMappingSchema = z.object({
	sourceField: z.string().optional(),
	sourceType: z.nativeEnum(FieldMappingSourceType).optional(),
	hubspotField: z.string().optional(),
	isActive: z.boolean().optional(),
	aiConfig: FieldMappingAiConfigSchema.optional(),
});

export type UpdateFieldMapping = z.infer<typeof UpdateFieldMappingSchema>;

// API Responses
export const HubspotApiKeyResponseSchema = z.object({
	success: z.boolean(),
});

export type HubspotApiKeyResponse = z.infer<typeof HubspotApiKeyResponseSchema>;

export const HubspotValidateResponseSchema = z.object({
	valid: z.boolean(),
	error: z.string().optional(),
});

export type HubspotValidateResponse = z.infer<typeof HubspotValidateResponseSchema>;

export const HubspotFieldsResponseSchema = z.object({
	entityType: z.nativeEnum(HubspotEntityType).optional(),
	fields: z.array(HubspotFieldSchema),
});

export type HubspotFieldsResponse = z.infer<typeof HubspotFieldsResponseSchema>;

export const SourceFieldsResponseSchema = z.object({
	contactFields: z.array(SourceFieldSchema),
	companyFields: z.array(SourceFieldSchema),
	dealFields: z.array(SourceFieldSchema).optional(),
	leadFields: z.array(SourceFieldSchema).optional(),
});

export type SourceFieldsResponse = z.infer<typeof SourceFieldsResponseSchema>;

export const FieldMappingsResponseSchema = z.array(FieldMappingSchema);

export type FieldMappingsResponse = z.infer<typeof FieldMappingsResponseSchema>;

// Bulk create mappings
export const BulkCreateFieldMappingsSchema = z.object({
	mappings: z.array(CreateFieldMappingSchema),
});

export type BulkCreateFieldMappings = z.infer<typeof BulkCreateFieldMappingsSchema>;

// Sync log
export const SyncLogSchema = z.object({
	id: z.number(),
	entityType: z.nativeEnum(HubspotEntityType),
	status: z.enum(["PENDING", "IN_PROGRESS", "SUCCESS", "PARTIAL", "FAILED"]),
	recordsSynced: z.number(),
	recordsFailed: z.number(),
	errorMessage: z.string().optional(),
	createdAt: z.string(),
	completedAt: z.string().optional(),
});

export type SyncLog = z.infer<typeof SyncLogSchema>;

export const SyncLogsResponseSchema = z.array(SyncLogSchema);

export type SyncLogsResponse = z.infer<typeof SyncLogsResponseSchema>;

// Sync Trigger Types
export enum HubspotSyncTrigger {
	DISABLED = "DISABLED",
	ON_CAMPAIGN_START = "ON_CAMPAIGN_START",
	ON_REPLY = "ON_REPLY",
}

export enum HubspotSyncTriggerWithInherit {
	INHERIT = "INHERIT",
	DISABLED = "DISABLED",
	ON_CAMPAIGN_START = "ON_CAMPAIGN_START",
	ON_REPLY = "ON_REPLY",
}

// Company Sync Trigger Response
export const CompanySyncTriggerResponseSchema = z.object({
	trigger: z.nativeEnum(HubspotSyncTrigger),
});

export type CompanySyncTriggerResponse = z.infer<typeof CompanySyncTriggerResponseSchema>;

// List Sync Trigger Response
export const ListSyncTriggerResponseSchema = z.object({
	trigger: z.nativeEnum(HubspotSyncTriggerWithInherit),
	effectiveTrigger: z.nativeEnum(HubspotSyncTrigger),
	isInherited: z.boolean(),
});

export type ListSyncTriggerResponse = z.infer<typeof ListSyncTriggerResponseSchema>;

// Update Sync Trigger DTOs
export const UpdateCompanySyncTriggerSchema = z.object({
	trigger: z.nativeEnum(HubspotSyncTrigger),
});

export type UpdateCompanySyncTrigger = z.infer<typeof UpdateCompanySyncTriggerSchema>;

export const UpdateListSyncTriggerSchema = z.object({
	trigger: z.nativeEnum(HubspotSyncTriggerWithInherit),
});

export type UpdateListSyncTrigger = z.infer<typeof UpdateListSyncTriggerSchema>;

// Deal Creation Trigger Types
export enum HubspotDealCreationTrigger {
	DISABLED = "DISABLED",
	ON_RESPONSE = "ON_RESPONSE",
	ON_POSITIVE_RESPONSE = "ON_POSITIVE_RESPONSE",
}

export enum HubspotDealCreationTriggerWithInherit {
	INHERIT = "INHERIT",
	DISABLED = "DISABLED",
	ON_RESPONSE = "ON_RESPONSE",
	ON_POSITIVE_RESPONSE = "ON_POSITIVE_RESPONSE",
}

// Company Deal Creation Trigger Response
export const CompanyDealCreationTriggerResponseSchema = z.object({
	trigger: z.nativeEnum(HubspotDealCreationTrigger),
});

export type CompanyDealCreationTriggerResponse = z.infer<
	typeof CompanyDealCreationTriggerResponseSchema
>;

// Update Company Deal Creation Trigger DTO
export const UpdateCompanyDealCreationTriggerSchema = z.object({
	trigger: z.nativeEnum(HubspotDealCreationTrigger),
});

export type UpdateCompanyDealCreationTrigger = z.infer<
	typeof UpdateCompanyDealCreationTriggerSchema
>;

// Lead Sync Trigger Types
export enum HubspotLeadSyncTrigger {
	DISABLED = "DISABLED",
	ON_RESPONSE = "ON_RESPONSE",
	ON_POSITIVE_RESPONSE = "ON_POSITIVE_RESPONSE",
}

export enum HubspotLeadSyncTriggerWithInherit {
	INHERIT = "INHERIT",
	DISABLED = "DISABLED",
	ON_RESPONSE = "ON_RESPONSE",
	ON_POSITIVE_RESPONSE = "ON_POSITIVE_RESPONSE",
}

export enum HubspotExistingLeadBehavior {
	SKIP = "SKIP",
	UPDATE = "UPDATE",
	CREATE_NEW = "CREATE_NEW",
}

export const CompanyLeadSyncTriggerResponseSchema = z.object({
	trigger: z.nativeEnum(HubspotLeadSyncTrigger),
});

export type CompanyLeadSyncTriggerResponse = z.infer<typeof CompanyLeadSyncTriggerResponseSchema>;

export const ListLeadSyncTriggerResponseSchema = z.object({
	trigger: z.nativeEnum(HubspotLeadSyncTriggerWithInherit),
	effectiveTrigger: z.nativeEnum(HubspotLeadSyncTrigger),
	isInherited: z.boolean(),
});

export type ListLeadSyncTriggerResponse = z.infer<typeof ListLeadSyncTriggerResponseSchema>;

export const UpdateCompanyLeadSyncTriggerSchema = z.object({
	trigger: z.nativeEnum(HubspotLeadSyncTrigger),
});

export type UpdateCompanyLeadSyncTrigger = z.infer<typeof UpdateCompanyLeadSyncTriggerSchema>;

export const UpdateListLeadSyncTriggerSchema = z.object({
	trigger: z.nativeEnum(HubspotLeadSyncTriggerWithInherit),
});

export type UpdateListLeadSyncTrigger = z.infer<typeof UpdateListLeadSyncTriggerSchema>;

// HubSpot Pipeline Stage
export const HubspotPipelineStageSchema = z.object({
	id: z.string(),
	label: z.string(),
	displayOrder: z.number().optional(),
});

export type HubspotPipelineStage = z.infer<typeof HubspotPipelineStageSchema>;

// HubSpot Pipeline
export const HubspotPipelineSchema = z.object({
	id: z.string(),
	label: z.string(),
	stages: z.array(HubspotPipelineStageSchema),
});

export type HubspotPipeline = z.infer<typeof HubspotPipelineSchema>;

export const HubspotPipelinesResponseSchema = z.object({
	pipelines: z.array(HubspotPipelineSchema),
});

export type HubspotPipelinesResponse = z.infer<typeof HubspotPipelinesResponseSchema>;

// HubSpot Owner
export const HubspotOwnerSchema = z.object({
	id: z.string(),
	email: z.string(),
	firstName: z.string().optional(),
	lastName: z.string().optional(),
});

export type HubspotOwner = z.infer<typeof HubspotOwnerSchema>;

export const HubspotOwnersResponseSchema = z.object({
	owners: z.array(HubspotOwnerSchema),
});

export type HubspotOwnersResponse = z.infer<typeof HubspotOwnersResponseSchema>;

// Deal Configuration
export const DealConfigSchema = z.object({
	defaultPipeline: z.string().optional().nullable(),
	defaultDealStage: z.string().optional().nullable(),
	dealNameTemplate: z.string().optional().nullable(),
	closeDateOffset: z.number().optional().nullable(),
	defaultContactOwner: z.string().optional().nullable(),
	defaultContactLifecycleStage: z.string().optional().nullable(),
	defaultContactLeadStatus: z.string().optional().nullable(),
	defaultCompanyOwner: z.string().optional().nullable(),
	defaultCompanyLifecycleStage: z.string().optional().nullable(),
	defaultCompanyLeadStatus: z.string().optional().nullable(),
});

export type DealConfig = z.infer<typeof DealConfigSchema>;

export const DealConfigResponseSchema = DealConfigSchema;

export type DealConfigResponse = z.infer<typeof DealConfigResponseSchema>;

// Lead Configuration
export const LeadConfigSchema = z.object({
	defaultPipeline: z.string().optional().nullable(),
	defaultLeadStage: z.string().optional().nullable(),
	leadNameTemplate: z.string().optional().nullable(),
	defaultLeadType: z.string().optional().nullable(),
	defaultLeadLabel: z.string().optional().nullable(),
	defaultOwner: z.string().optional().nullable(),
	existingLeadBehavior: z.nativeEnum(HubspotExistingLeadBehavior).optional().nullable(),
});

export type LeadConfig = z.infer<typeof LeadConfigSchema>;

export const LeadConfigResponseSchema = LeadConfigSchema;

export type LeadConfigResponse = z.infer<typeof LeadConfigResponseSchema>;

// User Mapping (Signalist user → HubSpot owner)
export const UserMappingSchema = z.object({
	id: z.number(),
	userId: z.number(),
	hubspotOwnerId: z.string(),
});

export type UserMapping = z.infer<typeof UserMappingSchema>;

export const UserMappingsResponseSchema = z.array(UserMappingSchema);

export type UserMappingsResponse = z.infer<typeof UserMappingsResponseSchema>;

// Create a single user mapping
export const CreateUserMappingSchema = z.object({
	userId: z.number(),
	hubspotOwnerId: z.string(),
});

export type CreateUserMapping = z.infer<typeof CreateUserMappingSchema>;

// Create HubSpot Deal
export const CreateHubspotDealRequestSchema = z.object({
	prospectId: z.number(),
	dealName: z.string().optional(),
	pipeline: z.string().optional(),
	dealStage: z.string().optional(),
	dealOwnerId: z.string().optional(),
	closeDate: z.string().optional(),
});

export type CreateHubspotDealRequest = z.infer<typeof CreateHubspotDealRequestSchema>;

export const CreateHubspotDealResponseSchema = CrmDealCreationResultSchema;

export type CreateHubspotDealResponse = z.infer<typeof CreateHubspotDealResponseSchema>;

// Conversation Sync
export const ConversationSyncResponseSchema = z.object({
	enabled: z.boolean(),
});

export type ConversationSyncResponse = z.infer<typeof ConversationSyncResponseSchema>;

// Manual Resync
export const HubspotResyncResponseSchema = z.object({
	queued: z.boolean(),
	queuedCount: z.number(),
	companyId: z.number(),
	triggeredAt: z.string(),
});

export type HubspotResyncResponse = z.infer<typeof HubspotResyncResponseSchema>;

export const UpdateConversationSyncSchema = z.object({
	enabled: z.boolean(),
});

export type UpdateConversationSync = z.infer<typeof UpdateConversationSyncSchema>;

// OAuth
export const OAuthAuthorizeResponseSchema = z.object({
	url: z.string(),
});

export type OAuthAuthorizeResponse = z.infer<typeof OAuthAuthorizeResponseSchema>;

export const OAuthStatusResponseSchema = z.object({
	connected: z.boolean(),
	hubspotPortalId: z.number().optional(),
	tokenExpiry: z.string().optional(),
	needsReauthorization: z.boolean().optional(),
});

export type OAuthStatusResponse = z.infer<typeof OAuthStatusResponseSchema>;

export const OAuthDisconnectResponseSchema = z.object({
	success: z.boolean(),
});

export type OAuthDisconnectResponse = z.infer<typeof OAuthDisconnectResponseSchema>;

// Sync Logs (PREC-4301): GET hubspot/sync/logs answers { logs, total }.
export const HubspotSyncLogSchema = z.object({
	id: z.number(),
	entityType: z.string(),
	status: z.string(),
	recordsSynced: z.number(),
	recordsFailed: z.number(),
	/** One entry per failed record: the message HubSpot or the sync gave, and the prospect. */
	errors: z
		.array(z.object({ error: z.string().optional(), prospectId: z.number().optional() }))
		.nullish(),
	startedAt: z.string().nullish(),
	completedAt: z.string().nullish(),
	createdAt: z.string(),
});

export type HubspotSyncLog = z.infer<typeof HubspotSyncLogSchema>;

export const HubspotSyncLogsResponseSchema = z.object({
	logs: z.array(HubspotSyncLogSchema),
	total: z.number(),
});

export type HubspotSyncLogsResponse = z.infer<typeof HubspotSyncLogsResponseSchema>;
